import React, { useMemo, useState } from 'react';
import {
  Alert, Breadcrumb, Button, Card, Checkbox, Col, Empty, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Spin, Statistic, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/common/MainLayout';
import { useListSavesQuery } from '../../api/savesV2Api';
import {
  useArchiveGardenPlanMutation,
  useCaptureGardenPlanMutation,
  useListGardenPlansQuery,
} from '../../api/gardenPlansApi';

const { Paragraph, Text, Title } = Typography;

interface CaptureValues {
  sourceSaveId: string;
  name: string;
  description?: string;
  sourceCalendarYear: number;
  includePlantings?: boolean;
}

const GardenPlansPage: React.FC = () => {
  const { t } = useTranslation('planning');
  const [captureOpen, setCaptureOpen] = useState(false);
  const [form] = Form.useForm<CaptureValues>();
  const { data: plans = [], isLoading, isError, refetch } = useListGardenPlansQuery({ status: 'active' });
  const { data: saves = [], isFetching: savesLoading } = useListSavesQuery({ type: 'free', status: 'active', limit: 100 });
  const [capture, { isLoading: capturing }] = useCaptureGardenPlanMutation();
  const [archive, { isLoading: archiving }] = useArchiveGardenPlanMutation();

  const saveOptions = useMemo(() => saves.map((save) => ({
    value: save.id,
    label: save.farmName || save.id,
  })), [saves]);

  const openCapture = () => {
    form.setFieldsValue({ sourceCalendarYear: new Date().getFullYear(), includePlantings: false });
    setCaptureOpen(true);
  };

  const submitCapture = async () => {
    try {
      const values = await form.validateFields();
      const { includePlantings, ...captureValues } = values;
      const result = await capture({
        ...captureValues,
        layers: { topology: true, plantings: includePlantings === true },
      }).unwrap();
      message.success(result.idempotent ? t('gardenPlans.toasts.alreadySaved') : t('gardenPlans.toasts.saved'));
      if (result.warnings?.length) message.warning(t('gardenPlans.toasts.capturedWithWarnings', { count: result.warnings.length }));
      form.resetFields();
      setCaptureOpen(false);
    } catch (error) {
      const apiError = error as { data?: { message?: string }; errorFields?: unknown };
      if (!apiError.errorFields) message.error(apiError.data?.message || t('gardenPlans.toasts.saveFailed'));
    }
  };

  const archivePlan = async (planId: string) => {
    try {
      await archive(planId).unwrap();
      message.success(t('gardenPlans.toasts.archived'));
    } catch (error) {
      const apiError = error as { data?: { message?: string } };
      message.error(apiError.data?.message || t('gardenPlans.toasts.archiveFailed'));
    }
  };

  return (
    <MainLayout breadcrumb={<Breadcrumb items={[{ title: <Link to="/">{t('gardenPlans.breadcrumbHome')}</Link> }, { title: t('gardenPlans.breadcrumb') }]} />}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>{t('gardenPlans.title')}</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>{t('common:actions.refresh')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCapture}>{t('gardenPlans.saveFromSave')}</Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('gardenPlans.info.title')}
        description={t('gardenPlans.info.description')}
      />

      {isError && <Alert type="error" message={t('gardenPlans.loadFailed')} style={{ marginBottom: 16 }} />}
      {isLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div> : plans.length === 0 ? (
        <Empty description={t('gardenPlans.empty')}>
          <Button type="primary" onClick={openCapture}>{t('gardenPlans.saveFirst')}</Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map((plan) => {
            const counts = plan.latestVersionSummary?.counts;
            return (
              <Col key={plan.id} xs={24} md={12} xl={8}>
                <Card
                  title={plan.name}
                  extra={(
                    <Popconfirm title={t('gardenPlans.archiveConfirm')} onConfirm={() => archivePlan(plan.id)}>
                      <Button danger type="text" icon={<DeleteOutlined />} loading={archiving} />
                    </Popconfirm>
                  )}
                >
                  {plan.description && <Paragraph ellipsis={{ rows: 2 }}>{plan.description}</Paragraph>}
                  <Row gutter={12}>
                    <Col span={8}><Statistic title={t('gardenPlans.counts.fields')} value={counts?.fields ?? 0} /></Col>
                    <Col span={8}><Statistic title={t('gardenPlans.counts.plots')} value={counts?.plots ?? 0} /></Col>
                    <Col span={8}><Statistic title={t('gardenPlans.counts.beds')} value={counts?.beds ?? 0} /></Col>
                  </Row>
                  <Space direction="vertical" size={2} style={{ marginTop: 12 }}>
                    <Text type="secondary">{t('gardenPlans.version', { version: plan.latestVersionId ? 'latest' : '—' })}</Text>
                    <Text type="secondary">{t('gardenPlans.scale', { scale: plan.latestVersionSummary?.planScale?.preset || '—' })}</Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        title={t('gardenPlans.captureModal.title')}
        open={captureOpen}
        onCancel={() => setCaptureOpen(false)}
        onOk={submitCapture}
        confirmLoading={capturing}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="sourceSaveId" label={t('gardenPlans.captureModal.sourceSave')} rules={[{ required: true, message: t('gardenPlans.captureModal.sourceSaveRequired') }]}>
            <Select loading={savesLoading} options={saveOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="name" label={t('gardenPlans.captureModal.name')} rules={[{ required: true, message: t('gardenPlans.captureModal.nameRequired') }, { max: 160 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sourceCalendarYear" label={t('gardenPlans.captureModal.calendarYear')} rules={[{ required: true }]}>
            <InputNumber min={1900} max={2200} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label={t('common:labels.description')}><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="includePlantings" valuePropName="checked">
            <Checkbox>{t('gardenPlans.captureModal.includePlantings')}</Checkbox>
          </Form.Item>
          <Alert type="info" showIcon message={t('gardenPlans.captureModal.intentsNote')} />
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default GardenPlansPage;
